import React, { useEffect, useState, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Image, Animated, Easing } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation } from '../context/NavigationContext';
import { useTheme } from '../context/ThemeContext';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useScrollNav } from '../hooks/useScrollNav';
import { useAuth } from '../context/AuthContext';
import RSKCurrency from './RSKCurrency';
import { rskService } from '../services/rskService';



const NavigationBar = () => {
  const navigation = useNavigation();
  const currentScreen = navigation.currentScreen;
  const { bannerEnabled } = navigation;
  const { palette, toggle, mode } = useTheme();
  const { isAtTop, isVisible } = useScrollNav('scroll-container');
  const { user } = useAuth();
  const translateY = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(1)).current;
  const prevIsAtTopRef = useRef(isAtTop);
  const bannerHeight = bannerEnabled ? 48 : 0; // Approximate banner height including padding
  const [logoHovered, setLogoHovered] = useState(false);
  const [rskBalance, setRskBalance] = useState(0);



  // Load RSK balance when user changes
  useEffect(() => {
    if (user?.uid) {
      loadRskBalance();
    } else {
      setRskBalance(0);
    }
  }, [user?.uid]);

  const loadRskBalance = async () => {
    if (!user?.uid) return;
    
    const { balance } = await rskService.getUserBalance(user.uid);
    setRskBalance(balance);
  };

  // Set up real-time RSK balance listener
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribe = rskService.subscribeToBalance(user.uid, (newBalance) => {
      setRskBalance(newBalance);
    });

    return unsubscribe;
  }, [user?.uid]);


  useEffect(() => {
    Animated.parallel([
      Animated.timing(translateY, {
        easing: Easing.out(Easing.ease),
        toValue: isVisible ? 0 : -80,
        duration: 180,
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        easing: Easing.out(Easing.ease),
        toValue: isVisible ? 1 : 0,
        duration: 180,
        useNativeDriver: true,
      }),
    ]).start();
  }, [isVisible]);


  useEffect(() => {
    // Just track direction; web will handle visual transitions via CSS
    prevIsAtTopRef.current = isAtTop;
  }, [isAtTop]);


  const renderNavItem = (text, isActive = false, onPress = () => { }) => {
    return (
      <TouchableOpacity onPress={onPress} style={styles.linkBtn}>
        <Text style={[
          styles.navItem, 
          { 
            color: isActive ? '#F7DF7B' : 'white',
            ...Platform.select({
              web: {
                textShadow: '2px 2px 1px rgba(0,0,0,1)',
              },
              default: {
                textShadowColor: 'rgba(0,0,0,1)',
                textShadowOffset: { width: 2, height: 2 },
                textShadowRadius: 1,
              }
            })
          }
        ]}>{text}</Text>
      </TouchableOpacity>
    );
  };

  const onSignIn = () => navigation.navigateTo('Login');
  const onGetStarted = () => navigation.navigateTo('GettingStarted');
  const onHome = () => navigation.navigateTo('Landing');
  const go = (screen) => navigation.navigateTo(screen);

  // Compute web-only animated style that animates blur/background/border on any direction change
  const shouldAnimateChange = prevIsAtTopRef.current !== isAtTop;

  const webBlurStyles = isAtTop
    ? {}
    : {
      backdropFilter: 'blur(10px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    };

  const webAnimatedStyle = Platform.select({
    web: {
      position: 'relative',
      zIndex: 10,
      ...webBlurStyles,
      backgroundColor: '#FF4747',
      boxShadow: '5px 7px 0px rgba(0,0,0,1)',
      border: `1px solid rgba(255, 255, 255, ${isAtTop ? 0 : 0.18})`,
      transition: shouldAnimateChange
        ? 'backdrop-filter 300ms ease, -webkit-backdrop-filter 300ms ease, background-color 300ms ease, border-color 300ms ease, box-shadow 300ms ease'
        : 'none',
    },
    default: {},
  });


  // Web-only animated wrapper positioning (top/left/right)
  const wrapperWebStyle = Platform.select({
    web: {
      position: 'fixed',
      top: (isAtTop ? 20 : 40) + bannerHeight,
      left: isAtTop ? 32 : 48,
      right: isAtTop ? 32 : 48,
      transition: shouldAnimateChange ? 'top 300ms ease, left 300ms ease, right 300ms ease' : 'none',
    },
    default: {},
  });

  return (
    <View style={[styles.navBarWrapper, wrapperWebStyle]}>

      <Animated.View
        style={[
          styles.navBar,
          // For native platforms, keep the glass vs transparent switch without animation of blur.
          Platform.OS === 'web' ? null : isAtTop ? styles.transparent : styles.glass,
          { transform: [{ translateY }], opacity },
          webAnimatedStyle,
        ]}
      >
        <View style={styles.navContent}>
          {/* Left Section - Logo */}
          <View style={styles.leftSection}>
          <TouchableOpacity 
            onPress={onHome} 
            style={styles.logoContainer}
            onMouseEnter={Platform.OS === 'web' ? () => setLogoHovered(true) : undefined}
            onMouseLeave={Platform.OS === 'web' ? () => setLogoHovered(false) : undefined}
          >
            <Image
              source={logoHovered ? require('../assets/favicon2.png') : require('../assets/favicon.png')}
              style={[
                styles.logoImage,
                Platform.OS === 'web' && {
                  transform: `rotate(-8deg) scale(${logoHovered ? 1.15 : 1})`,
                  transition: 'transform 0.3s ease, filter 0.3s ease',
                }
              ]}
              resizeMode="contain"
            />
          </TouchableOpacity>
          </View>

          {/* Center Section - Nav Links (only when logged in) */}
          {user && (
            <View style={styles.centerSection}>
            <View style={styles.centerNav}>
            {renderNavItem('My Dashboard', currentScreen === 'MyDashboard', () => go('MyDashboard'))}
            {renderNavItem('Courses', currentScreen === 'Courses', () => go('Courses'))}
            {renderNavItem('Settings', (currentScreen === 'Settings'), () => (user ? go('Settings') : onSignIn()))}
              {user?.userType === 'admin' && renderNavItem('Admin', currentScreen === 'Admin', () => go('Admin'))}
              </View>
            </View>
          )}

          {/* Right Section - RSK, Profile, Theme Toggle */}
          <View style={styles.rightSection}>
            {!user ? (
              // Not logged in - show Sign In / Get Started
              <>
                {renderNavItem('Sign In', currentScreen === 'Login', onSignIn)}
                {renderNavItem('Get Started', currentScreen === 'GettingStarted', onGetStarted)}
              </>
            ) : (
              // Logged in - show RSK, Profile, Theme Toggle
              <>
                {/* RSK Currency */}
                <View style={styles.rskContainer}>
                  <RSKCurrency amount={rskBalance} size="small" showLabel={true} />
                </View>

                {/* Profile */}
                <TouchableOpacity onPress={() => go('Profile')} style={styles.profileButton}>
                {user?.profilePhoto || user?.photoURL ? (
                  <Image
                    source={{ uri: user.profilePhoto || user.photoURL }}
                    style={styles.avatar}
                  />
                ) : (
                  <View style={[styles.avatar, styles.avatarPlaceholder, { borderColor: palette.border || 'rgba(255,255,255,0.18)' }]}>
                    <MaterialCommunityIcons name="account-outline" size={18} color={palette.text || 'white'} />
                  </View>
                )}
              </TouchableOpacity>
              </>
            )}

            {/* Theme toggle - always visible */}
            <TouchableOpacity 
              onPress={toggle} 
              style={[styles.themeToggle, { borderColor: 'rgba(255,255,255,0.3)', backgroundColor: 'rgba(0,0,0,0.2)' }]}
              accessibilityRole={Platform.OS === 'web' ? 'button' : undefined}
              accessibilityLabel={mode === 'dark' ? 'Switch to day mode' : 'Switch to night mode'}
            >
              <MaterialCommunityIcons
                name={mode === 'dark' ? 'weather-night' : 'white-balance-sunny'}
                size={18}
                color="white"
              />
            </TouchableOpacity>
          </View>
        </View>
      </Animated.View>
    </View>

  );
};

const styles = StyleSheet.create({
  navBar: {
    alignSelf: 'stretch',
    padding: 16,
    borderRadius: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    overflow: 'hidden',
    backgroundColor: '#FF4747',
    ...Platform.select({
      web: {
        boxShadow: '5px 7px 0px rgba(0,0,0,1)',
      },
      default: {
        shadowColor: '#000000',
        shadowOffset: { width: 5, height: 7 },
        shadowOpacity: 1,
        shadowRadius: 0,
        elevation: 8,
      }
    })
  },
  transparent: {
    backgroundColor: '#FF4747',
  },
  glass: {
    backgroundColor: '#FF4747',
    ...Platform.select({
      web: {
        position: 'relative',
        zIndex: 10,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.37)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
      },
      default: {
        elevation: 4,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.18)',
        zIndex: 10,
      },
    }),
  },
  navContent: {
    flex: 1,
    height: 32,
    paddingHorizontal: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 2,
  },
  centerSection: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    pointerEvents: 'box-none',
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    zIndex: 2,
  },
  logoContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  logoImage: {
    width: 56,
    height: 56,
  },
  centerNav: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    ...Platform.select({
      web: {
        pointerEvents: 'auto',
      },
    }),
  },
  linkBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  themeToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  themeToggleIcon: {
    fontSize: 14,
    lineHeight: 18,
  },
  themeToggleText: {
    fontSize: 14,
    fontFamily: 'Sen',
    fontWeight: '700',
    lineHeight: 18,
  },
  rskContainer: {
    marginRight: 4,
  },
  profileButton: {
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  navItem: {
    fontSize: 18,
    fontFamily: 'Sen',
    fontWeight: '700',
    lineHeight: 18,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  avatarPlaceholder: {
    backgroundColor: 'rgba(0,0,0,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  navBarWrapper: {
    position: 'absolute',
    top: 40,
    left: 48,
    right: 48,
    zIndex: 100,
    ...Platform.select({
      web: {
        position: 'fixed',
        top: 40,
      },
    }),
  },
});

export default NavigationBar;
